import React, { useRef, useState, useEffect } from "react";
import VoiceInput from "../../../shared/VoiceInput/VoiceInput";
import { ProjectAcceptFiles } from "../../../../data/config";
import { ConditionalRender } from "../../../shared/ConditionalRender/ConditionalRender";
import ProjectLoader from "../ProjectLoader/ProjectLoader";
import { AppMarkdown } from "../../../shared/AppMarkdown/AppMarkdown";
import StandardButton from "../../../shared/formComponents/StandardButton/StandardButton";
import "./Review.scss";
const ProjectChat = ({
  messages,
  loading,
  streamMessage,
  streamLoading,
  reasonLoading,
  reasonStreamText,
  messageLoading,
  setProjectChatFiles,
  projectChatFiles,
  isFileUploading,
  sendMessage,
  reasonElapsedSeconds,
  handleSendMessage,
  handleUploadChatFiles,
  showInput = true,
  showReadOnly = false,
  // showReadOnly also gates the auto-scroll-to-bottom-on-load behaviour
  // below (a static history view shouldn't force-scroll the way a live
  // chat does). This lets a consumer keep that scroll behaviour while
  // supplying its own note instead of the default top banner.
  hideReadOnlyBanner = false,
  remarkLoading,
  // Escape hatch for message senders this component doesn't natively know
  // about (e.g. the ai-xray/sprint divider rows). Called with (message,
  // index) for any sender not matched below; return a ReactNode to render
  // it, or null/undefined to render nothing for that message. Optional —
  // existing consumers that don't pass this see no change in behaviour.
  renderCustomMessage,
  // Optional node rendered once, after the last message/loading indicator
  // (right above the scroll anchor) — e.g. a subtle "read-only" note for a
  // finished conversation. Unused by default, so existing consumers are
  // unaffected.
  footerNote,
}) => {
  const [open, setOpen] = useState([]);
  const [reasonOpen, setReasonOpen] = useState(false);
  const hasInitialScrolled = useRef(false);
  const bottomRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const isSecondLastRemarkUpdater =
    messages.length >= 2 &&
    messages[messages.length - 2]?.sender === "assistant-remark-updater";
  useEffect(() => {
    if (showReadOnly) return;
    if (
      !hasInitialScrolled.current &&
      messages.length > 0 &&
      bottomRef.current
    ) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
      hasInitialScrolled.current = true;
    }
  }, [messages]);
  useEffect(() => {
    if (showReadOnly) return;
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [
    messageLoading,
    streamLoading,
    loading,
    remarkLoading,
    isSecondLastRemarkUpdater,
  ]);

  useEffect(() => {
    if (reasonLoading) {
      setReasonOpen(false);
    }
  }, [reasonLoading]);

  // Distance-from-bottom threshold below which we consider the user "at
  // the bottom" — small enough that the tail end of a message doesn't
  // falsely trigger the button, generous enough to tolerate rounding.
  const SCROLL_BOTTOM_THRESHOLD = 80;

  const checkScrollPosition = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollToBottom(distanceFromBottom > SCROLL_BOTTOM_THRESHOLD);
  };

  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    checkScrollPosition();
    el.addEventListener("scroll", checkScrollPosition);
    return () => el.removeEventListener("scroll", checkScrollPosition);
  }, []);

  // New content (a message arriving, or streaming text growing) can change
  // whether the user is within the "at bottom" threshold even without them
  // scrolling — re-check whenever the message list or any loading indicator
  // changes.
  useEffect(() => {
    checkScrollPosition();
  }, [messages, loading, streamLoading, reasonLoading, streamMessage]);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="d-flex flex-column px-4 flex-grow-1 overflow-auto h-100"
      id="chat-component"
    >
      {" "}
      <ConditionalRender condition={showReadOnly && !hideReadOnlyBanner}>
        <div className="read-only-container d-flex align-items-center justify-content-center py-3 w-100 mb-3">
          <span className="dark-grey">
            This is read-only. You can not chat here but can only read the chat
            history.
          </span>
        </div>
      </ConditionalRender>
      {/* position-relative, non-scrolling wrapper around the scrollable
          message list — .scroll-to-bottom-btn anchors to this instead of
          .project-chat-container itself, so it stays fixed in the visible
          viewport rather than scrolling away with the content (an
          absolutely positioned element scrolls with its containing block
          when that block is the scrolling element itself). */}
      <div className="chat-scroll-wrapper flex-grow-1 d-flex flex-column position-relative">
      <div
        className="project-chat-container flex-grow-1 overflow-auto d-flex flex-column align-items-center"
        ref={chatContainerRef}
      >
        <div className="w-100" id="topDiv">
          <>
            <ConditionalRender condition={messages.length > 0}>
              {messages.map((message, index) => {
                if (message.sender === "user") {
                  return (
                    <div
                      className={`${remarkLoading && index === messages.length - 1 ? "stream-message-remark-loading-height" : ""}`}
                    >
                      {/* fe_context — "Ask AI" highlighted snippet (ai-xray feature).
                          Only present when the message was sent via Ask AI; a plain
                          object with a selectedText field, or occasionally a raw string. */}
                      <ConditionalRender condition={!!message.fe_context}>
                        <div className="d-flex justify-content-end mt-2">
                          <div className="fe-context-chip">
                            <span className="fe-context-label">#context: </span>
                            <span>
                              {typeof message.fe_context === "object"
                                ? message.fe_context?.selectedText || ""
                                : message.fe_context}
                            </span>
                          </div>
                        </div>
                      </ConditionalRender>
                      <ConditionalRender condition={message.files.length > 0}>
                        <div className="d-flex flex-column align-items-end justify-content-end file-container mt-2">
                          {message.files.map((file, index) => (
                            <div
                              key={index}
                              className={`file-item p-2 ${message.files.length - 1 === index ? "mb-3" : ""}`}
                            >
                              <img
                                src="/assets/images/file-primary.svg"
                                alt=""
                                className="me-2"
                              />
                              <span className="small">
                                {file.fileName || file.name || "File"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </ConditionalRender>
                      <div
                        className={` d-flex align-items-end justify-content-end ${index === 0 || message.files.length > 0 ? "" : "mt-3"}`}
                      >
                        <div className="user-message p-3">
                          <span className="white-space-pre-wrap">
                            {message.message}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                } else if (
                  message.sender === "assistant" ||
                  message.sender === "reason_summary"
                ) {
                  const isOpen = open?.includes(index);
                  return (
                    <div
                      className={`${isSecondLastRemarkUpdater && messages.length - 1 === index ? "stream-message-remark-update-height" : ""} ${messages.length - 1 === index && !streamLoading ? "stream-message-height" : ""}`}
                    >
                      <ConditionalRender
                        condition={message.sender === "reason_summary"}
                      >
                        <div
                          className={`mt-3 d-flex align-items-center cursor-pointer `}
                          onClick={() => {
                            if (!isOpen) {
                              setOpen((prev) => [...prev, index]);
                            } else if (isOpen) {
                              setOpen((prev) =>
                                prev.filter((i) => i !== index),
                              );
                            }
                          }}
                        >
                          <img
                            src="/assets/images/agent-loading-icon.svg"
                            alt="agent-loading-icon"
                          />
                          <span className="small light-text mx-2">
                            Thought for {message.durationSec} second
                            {message.durationSec > 1 ? "s" : ""}
                          </span>
                          <img
                            src="/assets/images/down-arrow-dark.svg"
                            alt="down-arrow-dark"
                            className={`${isOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                      </ConditionalRender>
                      <ConditionalRender condition={isOpen}>
                        <div className="explanation-main p-3 d-flex flex-column">
                          <AppMarkdown>{message.message}</AppMarkdown>
                        </div>
                      </ConditionalRender>
                      <ConditionalRender
                        condition={message.sender === "assistant"}
                      >
                        <div className={`mt-3 d-flex align-items-start`}>
                          <img
                            src="/assets/images/agent-active-icon.svg"
                            alt="agent-active-icon"
                            className="me-2"
                          />
                          <div className="p-3 agent-response">
                            <AppMarkdown>{message.message}</AppMarkdown>
                            {/* actions — server-computed CTAs (e.g. sprint links),
                                same label the SME themselves would see (recomputed
                                server-side from their own sprint/ownership state).
                                Rendered as the same button, disabled — following it
                                would need the respondent's own session, not the
                                coach's, so it's shown for parity, not click-through. */}
                            <ConditionalRender
                              condition={message.actions?.length > 0}
                            >
                              <div className="message-actions mt-2 d-flex flex-wrap">
                                {(message.actions || []).map((action, i) => (
                                  <StandardButton
                                    key={i}
                                    className="sprint-action-btn"
                                    border={false}
                                    bold={true}
                                    disabled={true}
                                    text={action.label || "View"}
                                  />
                                ))}
                              </div>
                            </ConditionalRender>
                          </div>
                        </div>
                      </ConditionalRender>
                    </div>
                  );
                } else if (message.sender === "assistant-remark-updater") {
                  return (
                    <div>
                      <div className={`mt-3 d-flex align-items-center`}>
                        <img
                          src="/assets/images/agent-active-icon.svg"
                          alt="agent-active-icon"
                          className="me-2"
                        />
                        <div className="review-completed d-flex align-items-center">
                          <img
                            src="/assets/images/true-sucess.svg"
                            className="me-1"
                          />
                          {message.message}
                        </div>
                      </div>
                    </div>
                  );
                }
                // Any sender not matched above (e.g. divider_xray_completed,
                // divider_sprint_started/ended) — let the consumer render it
                // via renderCustomMessage, or render nothing if not provided
                // (same as the previous behaviour for unhandled senders).
                return renderCustomMessage
                  ? renderCustomMessage(message, index)
                  : null;
              })}
            </ConditionalRender>
            <ConditionalRender condition={loading}>
              <ProjectLoader loading={loading} />
            </ConditionalRender>
            <ConditionalRender condition={streamLoading}>
              <div
                className={`mt-3 d-flex align-items-start  ${messages.length > 0 && messages[messages.length - 1]?.sender === "user" ? "last-stream-message-height" : ""}`}
              >
                <img
                  src="/assets/images/agent-active-icon.svg"
                  alt="agent-active-icon"
                  className="me-2"
                />
                <div className="p-3 agent-response">
                  <AppMarkdown>{streamMessage}</AppMarkdown>
                </div>
              </div>
            </ConditionalRender>
            <ConditionalRender condition={reasonLoading}>
              <div className="stream-message-height">
                <div
                  className={`mt-3 d-flex align-items-center cursor-pointer `}
                  onClick={() => {
                    setReasonOpen(!reasonOpen);
                  }}
                >
                  <img
                    src="/assets/images/agent-loading-icon.svg"
                    alt="agent-loading-icon"
                  />
                  <span className="small light-text mx-2">
                    Thought for {reasonElapsedSeconds} second
                    {reasonElapsedSeconds > 1 ? "s" : ""}
                  </span>
                  <img
                    src="/assets/images/down-arrow-dark.svg"
                    alt="down-arrow-dark"
                    className={`${reasonOpen ? "rotate-180" : ""}`}
                  />
                </div>
                <ConditionalRender condition={reasonOpen}>
                  <div className="explanation-main p-3 d-flex flex-column">
                    <AppMarkdown>{reasonStreamText}</AppMarkdown>
                  </div>
                </ConditionalRender>
              </div>
            </ConditionalRender>
          </>
          <div ref={bottomRef} />
        </div>
      </div>
      <ConditionalRender condition={showScrollToBottom}>
        <button
          type="button"
          className="scroll-to-bottom-btn"
          onClick={scrollToBottom}
          aria-label="Scroll to latest message"
        >
          <i className="fa-solid fa-arrow-down"></i>
        </button>
      </ConditionalRender>
      </div>
      {/* Pinned below the scrollable message area (not inside it), so it's
          always visible without needing to scroll — same footer slot the
          input bar occupies for a live chat, just with a static note
          instead for a finished, read-only one. */}
      <ConditionalRender condition={!!footerNote}>
        <div className="chat-footer-note">{footerNote}</div>
      </ConditionalRender>
      <ConditionalRender condition={showInput}>
        <div className="mt-auto">
          <VoiceInput
            value={""}
            placeholder="Reply here..."
            onSend={(text) => {
              handleSendMessage(text);
            }}
            onPlus={(files) => {
              handleUploadChatFiles(files);
            }}
            onClear={() => {
              // console.log("Cleared");
            }}
            onMicResult={(chunk) => {
              console.log("Mic chunk:", chunk);
            }}
            commitMode="replace"
            fileAccept={ProjectAcceptFiles}
            fileMultiple={true}
            isLoading={false}
            showMic={true}
            onCancel={() => {}}
            disabled={streamLoading || reasonLoading}
            isFileUploading={isFileUploading}
            files={projectChatFiles}
            handleRemoveFile={(files) => {
              setProjectChatFiles(files);
            }}
          />
        </div>
      </ConditionalRender>
    </div>
  );
};

export default ProjectChat;
